import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Kanban as KanbanIcon, ChevronRight, MapPin, Calendar, CheckCircle2, Search, X, AlertTriangle, MessageSquareWarning } from "lucide-react";
import { useBiens, useUpdateBien, useBienContacts } from "@/hooks/useBiens";
import { useBiensPermRole } from "@/hooks/useBiensPermRole";
import { useProfiles } from "@/hooks/useProfiles";
import { usePrestataires } from "@/hooks/usePrestataires";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import { cn } from "@/lib/utils";
import type { BienRow, BienUpdate } from "@/schemas/bien";
import { filterVisibleBiens, getLifecycleStage } from "@/v2/lib/bienLifecycle";
import { isElsLance, isPebLance, isPebDone } from "@/lib/bienPrepValidation";
import { formatDate, localeFromLng } from "@/v2/lib/formatDate";
import { CheckElsBadges, checkElsStates } from "@/v2/components/CheckElsBadges";
import { isTransfertElecFait } from "@/lib/elsStatus";
import { getPebStatut } from "@/lib/pebCycle";
import { BienDetailModal } from "@/v2/components/BienDetailModal";
import { PipelineActionsPopover } from "@/v2/components/PipelineActionsPopover";
import { useLaunchMission } from "@/hooks/useLaunchMission";
import { useToast } from "@/components/ui/Toast";
import { SelectField } from "@/components/ui/SelectField";

interface KanbanCol {
  key: string;
  label: string;
  desc: string;
  color: string;
  filter: (b: BienRow) => boolean;
}

/**
 * Kanban pipeline pré-location pour le collaborateur.
 *
 * Source : statuts du bien (statutEls, dossierCloture, dossierClos,
 * appartPretPhotos). Pas de table tasks — tout dérivé.
 *
 * Les colonnes représentent l'étape métier; un drag entre colonnes serait
 * naturel (changer le statut), mais V1 livre une vue read-only avec navigation
 * vers la section "ELS" du bien pour éditer.
 */
export function CollabPipelinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { role } = useRole();
  const { data: biens, isLoading } = useBiens();
  const { data: profiles = [] } = useProfiles();
  const { data: prestataires = [] } = usePrestataires();
  // Params d'arrivée depuis le Dashboard CLevel (clic "ELS par collaborateur") :
  //   ?collab=<id>  → pré-sélectionne le collaborateur
  //   ?focus=<col>  → surligne + scroll la colonne (progression)
  //   ?highlight=<peb|transfert|photos> → cercle les cartes concernées (alertes)
  const [searchParams] = useSearchParams();
  const collabParam = searchParams.get("collab");
  const focusKey = searchParams.get("focus");
  const highlightParam = searchParams.get("highlight");
  // Modale fiche bien — pour ne pas perdre le contexte du kanban (filtres,
  // scroll, focus), on ouvre la fiche en surimpression au lieu de naviguer
  // vers /v2/biens/:id.
  const [modalBienId, setModalBienId] = useState<string | null>(null);
  // Popover d'actions ancré (clic sur une card) — remplace l'ouverture directe
  // de la fiche entière. La fiche reste accessible via "Ouvrir la fiche complète".
  const [popover, setPopover] = useState<{ bienId: string; rect: DOMRect } | null>(null);
  // Contact scopé au bien du popover ouvert (collaborateurSrs/expertPeb/
  // prestataireEle) — jamais toute la table profils. Voir migration 126.
  const { data: popoverContacts = [] } = useBienContacts(popover?.bienId);

  // Assignation ELS inline depuis le popover (sans ouvrir la fiche). Le rôle
  // DOIT être remappé (collaborateur → collaborateur_afr, cf. useBiensPermRole)
  // comme sur la fiche détail : sans ça, la RPC filtre `prestataireEle` (non
  // éditable pour `collaborateur` brut) mais garde les 2 champs de refus du
  // même patch (eux le sont) — l'appel "réussit" sans lever d'erreur, et
  // l'assignation ne part jamais. Bug signalé 2026-08-19 : "je le choisis...
  // il ne se passe rien".
  const permRole = useBiensPermRole();
  const assignMut = useUpdateBien(popover?.bienId ?? "", permRole);
  const elsPrestaOptions = useMemo(
    () =>
      profiles
        .filter((p) => p.role === "prestataire" && p.statut !== "inactif" && (p.specialites ?? []).includes("etat-des-lieux"))
        .map((p) => ({ value: p.id, label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email || p.id })),
    [profiles],
  );
  // Miroir ELS pour PEB — le popover n'avait AUCUN moyen de définir un expert
  // PEB non encore assigné (signalé Thibaud 2026-08-19 : "pas uniforme").
  const pebExpertOptions = useMemo(
    () =>
      profiles
        .filter((p) => p.role === "prestataire" && p.statut !== "inactif" && (p.specialites ?? []).includes("expert-peb"))
        .map((p) => ({ value: p.id, label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email || p.id })),
    [profiles],
  );
  // (Ré)assigner le "Prestataire assigné" + lever un éventuel refus en attente.
  // Le popover reste OUVERT après l'assignation (pas de setPopover(null) ici) :
  // il est dérivé de `bien`, donc il bascule tout seul sur "Lancer la mission"
  // dès que la mutation aboutit et rafraîchit le cache — sans ça, l'utilisateur
  // devait recliquer sur la card pour voir l'étape suivante (signalé Thibaud
  // 2026-08-19 : "pas ergonomique").
  // `assigningId` : repère de CE choix précis, le temps de l'aller-retour
  // réseau — sans lui, rien ne distingue "j'ai bien cliqué" de "il ne se
  // passe rien" pendant les quelques centaines de ms d'attente (signalé
  // Thibaud 2026-08-19).
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const handleAssignEls = (id: string) => {
    setAssigningId(id);
    assignMut.mutate(
      { prestataireEle: id, prestataireEleRefusePar: "", prestataireEleRefuseAt: null },
      { onSettled: () => setAssigningId(null) },
    );
  };
  const handleAssignPeb = (id: string) => {
    setAssigningId(id);
    assignMut.mutate(
      { expertPeb: id, expertPebRefusePar: "", expertPebRefuseAt: null },
      { onSettled: () => setAssigningId(null) },
    );
  };

  // Lancement manuel d'une mission (ELS/PEB) : email + visibilité prestataire.
  const toast = useToast();
  const launchMut = useLaunchMission();

  // Actions "reste à faire" (photos + clôture SRS) directement depuis le
  // popover, sans ouvrir la fiche entière — signalé Thibaud 2026-08-20 (veille
  // d'une démo) : "pas moyen de me faire juste la popup avec ces champs-là ?
  // c'est tellement plus ergonomique... il faut se mettre à leur place".
  // Même mutation `assignMut` que l'assignation ELS/PEB (rôle déjà remappé
  // collaborateur_afr), même toast d'erreur global (meta.toastOnError sur
  // useUpdateBien) — aucune duplication de logique d'écriture.
  const handleSetPhotosReady = (value: boolean) => {
    assignMut.mutate({ appartPretPhotos: value });
  };
  const handleUpdateSrsField = (patch: BienUpdate) => {
    assignMut.mutate(patch);
  };
  // Ferme le popover au succès (contrairement à l'assignation/lancement,
  // volontairement laissés ouverts) : cette action fait CHANGER le bien de
  // colonne (els_done → closing), donc le popover ré-affiché montre un tout
  // autre bloc (validation finale C-Level) sur une card qui n'est plus au
  // même endroit — perçu comme un popup "resté là" incompréhensible (signalé
  // Thibaud 2026-08-20 : "je viens de clôturer... il reste cette popup
  // là... c'est pas compréhensible"). Même raisonnement que
  // `handleValiderClotureSrs` ci-dessous, qui fermait déjà pour la même
  // raison au changement de colonne suivant.
  const handleCloturerSrs = () => {
    assignMut.mutate(
      { dossierCloture: true },
      {
        onSuccess: () => {
          toast.success(t("v2.pipeline.actions.srsClotureSuccess"));
          setPopover(null);
        },
      },
    );
  };
  // Étape 2 (C-Level/admin), distincte de dossierCloture ci-dessus — même
  // geste que le 2e bouton de la fiche complète (cf. BienDetailPage).
  // Contrairement aux autres actions du popover (assignation, lancement,
  // clôture étape 1) qui restent volontairement ouvertes pour montrer l'état
  // qui vient de changer, celle-ci fait QUITTER le bien du pipeline (stage
  // "loue", terminal) — le popover doit donc se fermer, sinon il reste
  // affiché "orphelin" sur une card qui n'est plus dans la colonne (signalé
  // Thibaud 2026-08-20 : "cette popup qui est resté... derrière la bonne
  // popup").
  const handleValiderClotureSrs = () => {
    assignMut.mutate(
      { dossierClos: true },
      {
        onSuccess: () => {
          toast.success(t("biens.dossierClosSuccess"));
          setPopover(null);
        },
      },
    );
  };
  // `launchingMission` repère LAQUELLE des deux missions est en vol — sans ça,
  // `launchMut.isPending` (partagé) désactivait les DEUX boutons "Lancer" au
  // clic sur un seul, donnant l'impression que les deux se lançaient ensemble
  // (signalé Thibaud 2026-08-19 : "pas terrible... on doit pouvoir les lancer
  // séparément"). Chaque bouton ne réagit plus qu'à SA propre mission.
  const [launchingMission, setLaunchingMission] = useState<"els" | "peb" | null>(null);
  // Le popover reste OUVERT après un lancement (pas de setPopover(null) ici,
  // même raison que handleAssignEls) : le fermer aussitôt faisait disparaître
  // les DEUX blocs ELS et PEB d'un coup, laissant croire que les deux
  // missions venaient d'être lancées alors qu'une seule l'était vraiment
  // (signalé Thibaud 2026-08-19). Rester ouvert montre clairement laquelle a
  // basculé sur "lancée le ..." et laquelle reste à lancer.
  const handleLaunch = (bienId: string, mission: "els" | "peb") => {
    setLaunchingMission(mission);
    launchMut.mutate(
      { bienId, mission, role: role ?? "collaborateur" },
      {
        onSuccess: (r) => {
          toast.success(
            r.emailSent === false
              ? t("v2.pipeline.actions.launchedNoEmail")
              : t("v2.pipeline.actions.launchedOk"),
          );
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
        onSettled: () => setLaunchingMission(null),
      },
    );
  };

  // Le collaborateur ne voit PAS la 4e colonne "SRS admin à clôturer"
  // (validation finale = C-Level / Admin). Une fois le dossier finalisé
  // (dossierCloture=true → stage "closing"), le bien quitte son board.
  const isCollab = (role ?? "").startsWith("collaborateur");

  const allCols: KanbanCol[] = [
    // Filtres basés sur getLifecycleStage : un bien a EXACTEMENT un stage,
    // donc impossible d'apparaître dans 2 colonnes. La fonction priorise
    // les flags de clôture sur edlDatePrevue, donc un bien avec
    // dossierCloture=true mais sans date ELS tombe correctement dans
    // "SRS ADMIN À CLÔTURER" et nulle part ailleurs.
    {
      key: "ready-for-els",
      label: t("v2.pipeline.columns.ready-for-els.label"),
      desc: t("v2.pipeline.columns.ready-for-els.desc"),
      color: "bg-warning/10 border-warning/30",
      // « À planifier » et « À lancer » partagent la 1re colonne (2026-09-15).
      filter: (b) => { const s = getLifecycleStage(b).stage; return s === "ready_for_els" || s === "a_lancer"; },
    },
    {
      key: "els-fixee",
      label: t("v2.pipeline.columns.els-fixee.label"),
      desc: t("v2.pipeline.columns.els-fixee.desc"),
      color: "bg-accent/10 border-accent/30",
      filter: (b) => getLifecycleStage(b).stage === "els_planned",
    },
    {
      key: "els-fait",
      label: t("v2.pipeline.columns.els-fait.label"),
      desc: t("v2.pipeline.columns.els-fait.desc"),
      color: "bg-warning/15 border-warning/40",
      filter: (b) => getLifecycleStage(b).stage === "els_done",
    },
    {
      key: "srs-admin",
      label: t("v2.pipeline.columns.srs-admin.label"),
      desc: t("v2.pipeline.columns.srs-admin.desc"),
      color: "bg-success/10 border-success/30",
      filter: (b) => getLifecycleStage(b).stage === "closing",
    },
  ];

  const cols = allCols.filter((c) => !(isCollab && c.key === "srs-admin"));

  // Garde-fou métier : on filtre AVANT tout par la visibilité du rôle
  // (les biens dont CLevel n'a pas fini la prep ne remontent pas).
  // En vue C-Level, on garde tout pour audit.
  const { visible: gateFiltered, blocked: blockedCount } = useMemo(
    () => filterVisibleBiens(biens || [], role),
    [biens, role],
  );

  // Collaborateur → initiales (mini-cards) + nom complet au survol. Même
  // dérivation que la colonne "Col SL" du workspace Publication.
  const collabMap = useMemo(() => {
    const m: Record<string, { init: string; name: string }> = {};
    const add = (id: string, fn: string, ln: string, email: string) => {
      const name = `${fn} ${ln}`.trim() || email;
      const init = `${fn[0] ?? ""}${ln[0] ?? ""}`.toUpperCase() || (email?.[0] ?? "?").toUpperCase();
      m[id] = { init, name };
    };
    profiles.forEach((p) => add(p.id, (p.first_name ?? "").trim(), (p.last_name ?? "").trim(), p.email));
    // Filet : si le profil loggé n'est pas dans la liste, on l'ajoute quand même.
    if (profile?.id && !m[profile.id]) {
      add(profile.id, (profile.first_name ?? "").trim(), (profile.last_name ?? "").trim(), profile.email);
    }
    return m;
  }, [profiles, profile]);

  // Options du sélecteur : "Tous" + chaque collaborateur (rôle collaborateur*).
  const collabOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    profiles
      .filter((p) => (p.role ?? "").startsWith("collaborateur") && p.statut !== "inactif")
      .forEach((p) => { seen.add(p.id); opts.push({ value: p.id, label: collabMap[p.id]?.name ?? p.email }); });
    // Garantit la présence du collab loggé même s'il n'est pas dans la liste.
    if (profile?.id && (role ?? "").startsWith("collaborateur") && !seen.has(profile.id)) {
      opts.push({ value: profile.id, label: collabMap[profile.id]?.name ?? profile.email });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, "fr"));
    return [{ value: "all", label: t("v2.pipeline.allCollaborators") }, ...opts];
  }, [t, profiles, collabMap, profile, role]);

  // Sélection courante. Priorité : param ?collab= (depuis le Dashboard) >
  // défaut rôle ("all" pour C-Level/Admin, collab loggé pour un collaborateur).
  const [selectedCollab, setSelectedCollab] = useState<string>("all");
  const collabInit = useRef(false);
  // Param ?collab= : pilote la sélection à chaque changement (re-clic depuis le
  // Dashboard sur un autre collaborateur → la vue suit).
  useEffect(() => {
    if (collabParam) {
      setSelectedCollab(collabParam);
      collabInit.current = true;
    }
  }, [collabParam]);
  // Défaut rôle, une seule fois, uniquement si pas de param ?collab=.
  useEffect(() => {
    if (collabInit.current || collabParam || !role) return;
    const isCollab = role.startsWith("collaborateur");
    if (isCollab && !profile?.id) return; // attendre le profil avant de figer
    if (isCollab && profile?.id) setSelectedCollab(profile.id);
    collabInit.current = true;
  }, [role, profile?.id, collabParam]);

  const myBiens = useMemo(() => {
    if (selectedCollab === "all") return gateFiltered;
    return gateFiltered.filter((b) => b.collaborateurSrs === selectedCollab);
  }, [gateFiltered, selectedCollab]);

  // Total des biens RÉELLEMENT dans le pipeline (= somme des 4 stages
  // opérationnels). Exclut les biens en draft_clevel, loué, vendu, live,
  // ready_to_market. C'est ce chiffre qu'on affiche dans le sous-titre.
  const totalInPipeline = useMemo(() => {
    return myBiens.filter((b) => {
      const s = getLifecycleStage(b).stage;
      // "closing" n'est compté que pour les rôles qui voient la 4e colonne.
      return s === "ready_for_els" || s === "a_lancer" || s === "els_planned" || s === "els_done" || (!isCollab && s === "closing");
    }).length;
  }, [myBiens, isCollab]);

  // Recherche : ne FILTRE pas le kanban (sinon on perd le contexte étape).
  // À la place : matche un bien → highlight vert + non-match grisés à 25%.
  // L'œil voit immédiatement où le bien se situe dans le pipeline.
  const [search, setSearch] = useState("");
  const searchLower = search.trim().toLowerCase();
  const matches = (b: BienRow) => {
    if (!searchLower) return true;
    return (
      [b.referenceInterne, b.reference, b.id, b.adresse, b.ville, b.projet, b.typeBien]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchLower)
    );
  };
  const totalMatches = searchLower ? myBiens.filter(matches).length : 0;

  // Mise en avant des cartes "alertes transverses" (?highlight=peb|transfert|
  // photos) — mêmes critères que les compteurs du Dashboard CLevel. Les cartes
  // affichées dans le pipeline sont déjà actives (les colonnes excluent
  // loué/vendu), donc pas besoin de re-tester isActive.
  const matchesHighlight = (b: BienRow): boolean => {
    if (!highlightParam) return false;
    if (highlightParam === "peb") return checkElsStates(b).peb !== "success";
    const edl = b.edlDatePrevue ? new Date(b.edlDatePrevue) : null;
    const edlPast = !!edl && !isNaN(edl.getTime()) && edl <= new Date();
    if (highlightParam === "transfert") return !isTransfertElecFait(b) && edlPast;
    if (highlightParam === "photos")
      return (b.appartPretPhotos === null || b.appartPretPhotos === undefined) && edlPast;
    return false;
  };

  // Highlight + auto-scroll vers une colonne quand on arrive avec ?focus=<key>
  // (ex. clic sur la tuile "SRS admin à clôturer" du Dashboard → focus=srs-admin).
  // Le highlight dure 3 secondes puis s'estompe pour ne pas être permanent.
  const colRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [highlightActive, setHighlightActive] = useState(false);

  // On attend `!isLoading` (colonnes rendues → colRefs peuplés) avant d'activer.
  // Sinon, en navigation directe (clic Dashboard), les données ne sont pas
  // encore hydratées à 100ms → l'ancien code sortait avant d'allumer le
  // highlight et la colonne n'était jamais surlignée.
  useEffect(() => {
    if (!focusKey || isLoading) return;
    const focusT = setTimeout(() => {
      const el = colRefs.current[focusKey];
      // block: "start" ramène le haut de la colonne au-dessus du fold ;
      // inline: "center" centre horizontalement (sidebar + header à gauche).
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" });
      setHighlightActive(true);
    }, 80);
    const fadeT = setTimeout(() => setHighlightActive(false), 3600);
    return () => { clearTimeout(focusT); clearTimeout(fadeT); };
  }, [focusKey, isLoading]);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <KanbanIcon size={22} className="text-accent" />
            {t("v2.nav.pipelineEls")}
          </h1>
          <p className="text-sm text-text-muted mt-1">
            {selectedCollab === "all"
              ? t("v2.pipeline.subtitleAll", { count: totalInPipeline })
              : t("v2.pipeline.subtitleCollab", {
                  name: collabMap[selectedCollab]?.name ?? t("v2.pipeline.collaborator"),
                  count: totalInPipeline,
                })}
          </p>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <SelectField
            label={t("v2.pipeline.collaborator")}
            value={selectedCollab}
            onChange={setSelectedCollab}
            options={collabOptions}
            className="w-full sm:w-56"
          />
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("v2.pipeline.searchPlaceholder")}
              className="w-full pl-8 pr-8 py-2 text-sm rounded-md border border-border bg-surface outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary p-0.5 cursor-pointer"
                title={t("v2.pipeline.clearSearch")}
              >
                <X size={14} />
              </button>
            )}
            {searchLower && (
              <div className="absolute -bottom-5 left-1 text-[11px] text-text-muted">
                {t("v2.pipeline.searchResults", { count: totalMatches })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bandeau bloqueurs CLevel — visible uniquement si l'utilisateur peut
          agir dessus (C-Level / Admin). Le collab voit juste son pipeline
          propre, sans s'inquiéter de ce qu'il n'a pas le pouvoir de débloquer. */}
      {blockedCount > 0 && (role === "c_level" || role === "admin") && (
        <button
          type="button"
          onClick={() => navigate("/v2/biens?filter=blocked")}
          className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-md border border-danger/40 bg-danger/5 text-danger hover:bg-danger/10 cursor-pointer text-left transition-colors"
        >
          <AlertTriangle size={18} className="flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold">
              {t("v2.pipeline.blockedTitle", { count: blockedCount })}
            </div>
            <div className="text-xs text-danger/80">
              {t("v2.pipeline.blockedDesc")}
            </div>
          </div>
          <ChevronRight size={16} className="flex-shrink-0" />
        </button>
      )}

      {isLoading ? (
        <div className="text-center text-text-muted py-12">{t("common.loading")}</div>
      ) : (
        <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-3", isCollab ? "lg:grid-cols-3" : "lg:grid-cols-4")}>
          {cols.map((col) => {
            const bs = myBiens.filter(col.filter);
            const isFocused = focusKey === col.key && highlightActive;
            return (
              <div
                key={col.key}
                ref={(el) => { colRefs.current[col.key] = el; }}
                className={cn(
                  "flex flex-col min-h-[60vh] transition-all rounded-md",
                  isFocused && "ring-4 ring-accent ring-offset-2 animate-pulse",
                )}
              >
                <div className={cn("rounded-t-md border-2 border-b-0 px-3 py-2", col.color)}>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-text-primary">
                    {col.label}
                  </h3>
                  <p className="text-[11px] text-text-muted">{col.desc}</p>
                  <div className="mt-1 text-xs font-medium text-text-secondary">
                    {t("v2.pipeline.bienCount", { count: bs.length })}
                  </div>
                </div>
                <div className="flex-1 bg-background/50 border-2 border-t-0 border-border rounded-b-md p-2 flex flex-col gap-2 overflow-y-auto">
                  {bs.length === 0 ? (
                    <div className="text-center text-text-muted/60 text-xs py-6">
                      <CheckCircle2 size={20} className="mx-auto mb-1 opacity-40" />
                      {t("v2.pipeline.empty")}
                    </div>
                  ) : (
                    bs.map((b) => {
                      const isMatch = matches(b);
                      const isCrit = matchesHighlight(b);
                      const isDimmed = !!searchLower && !isMatch;
                      const isHighlight = (!!searchLower && isMatch) || isCrit;
                      return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={(e) => setPopover({ bienId: b.id, rect: e.currentTarget.getBoundingClientRect() })}
                        className={cn(
                          "w-full text-left bg-surface border rounded-md p-2.5 hover:border-accent cursor-pointer transition-all group",
                          isDimmed && "opacity-25 hover:opacity-60",
                          isHighlight && "border-accent ring-2 ring-accent/30 shadow-sm",
                          !isDimmed && !isHighlight && "border-border",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-sm font-mono font-semibold text-text-primary truncate">
                            {b.referenceInterne || b.reference || "—"}
                          </span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {collabMap[b.collaborateurSrs || ""] && (
                              <span
                                title={collabMap[b.collaborateurSrs || ""].name}
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-surface-2 text-text-secondary text-[9px] font-semibold"
                              >
                                {collabMap[b.collaborateurSrs || ""].init}
                              </span>
                            )}
                            <ChevronRight
                              size={12}
                              className="text-text-muted group-hover:text-accent"
                            />
                          </div>
                        </div>
                        <div className="text-sm font-medium text-text-primary truncate">
                          {b.adresse || "—"}
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-[11px] text-text-muted">
                          <MapPin size={10} />
                          <span className="truncate">{b.ville || "—"}</span>
                        </div>
                        {/* Surfacing refus prestataire (migration 055/068) — à réassigner */}
                        {(b.prestataireEleRefuseAt || b.expertPebRefuseAt) && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {b.prestataireEleRefuseAt && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger/10 text-danger">
                                <AlertTriangle size={10} /> {t("v2.pipeline.elsRefuse")}
                              </span>
                            )}
                            {b.expertPebRefuseAt && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger/10 text-danger">
                                <AlertTriangle size={10} /> {t("v2.pipeline.pebRefuse")}
                              </span>
                            )}
                          </div>
                        )}
                        <DateLines bien={b} collabMap={collabMap} />

                        {/* Remarques prestataires + collab — ligne dédiée juste
                            au-dessus du Check ELS. Badge rouge uniforme, tooltip
                            natif = contenu intégral. N'affiche que les sources
                            qui ont effectivement un commentaire. */}
                        <RemarqueBadges
                          pebCommentaire={b.pebCommentaire}
                          edlCommentaire={b.edlCommentaire}
                          commentaireSrs={b.commentaireSrs}
                        />
                        {/* Check ELS — 5 pastilles compactes (PEB/ELS/Élec/Picts/SRS) */}
                        <div className="mt-2 pt-2 border-t border-border/40">
                          <CheckElsBadges bien={b} size="xs" showLabels />
                        </div>
                      </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <BienDetailModal bienId={modalBienId} onClose={() => setModalBienId(null)} preset="pipeline_els" />

      {popover && (() => {
        const b = (biens || []).find((x) => x.id === popover.bienId);
        if (!b) return null;
        return (
          <PipelineActionsPopover
            bien={b}
            rect={popover.rect}
            prestataires={prestataires}
            profiles={popoverContacts}
            elsOptions={elsPrestaOptions}
            pebOptions={pebExpertOptions}
            onAssignEls={handleAssignEls}
            onAssignPeb={handleAssignPeb}
            assigningId={assigningId}
            onLaunch={(mission) => handleLaunch(popover.bienId, mission)}
            launchingEls={launchingMission === "els"}
            launchingPeb={launchingMission === "peb"}
            onSetPhotosReady={handleSetPhotosReady}
            onUpdateSrsField={handleUpdateSrsField}
            onCloturerSrs={handleCloturerSrs}
            onValiderClotureSrs={handleValiderClotureSrs}
            savingSrs={assignMut.isPending}
            onOpenFiche={() => { setModalBienId(popover.bienId); setPopover(null); }}
            onClose={() => setPopover(null)}
          />
        );
      })()}
    </div>
  );
}

// ─── Sub-component : lignes date ELS + PEB ────────────────────────────────

/** Nom de l'expert/prestataire pour l'affichage "(Nom)" derrière ELS/PEB.
 *  Gère les valeurs spéciales "amiable" / "autre". Null si non assigné. */
function expertLabel(
  id: string | null | undefined,
  collabMap: Record<string, { name: string }>,
  t: (key: string) => string,
): string | null {
  if (!id) return null;
  if (id === "amiable") return t("v2.pipeline.expert.amiable");
  if (id === "autre") return t("v2.pipeline.expert.autre");
  return collabMap[id]?.name ?? null;
}

type DateTone = "green" | "red" | "muted";

function DateLines({ bien, collabMap }: { bien: BienRow; collabMap: Record<string, { init: string; name: string }> }) {
  const { t, i18n } = useTranslation();
  const stage = getLifecycleStage(bien).stage;
  // Col 3 (à clôturer collab), col 4 (SRS admin) et états terminaux : les dates
  // ELS/PEB ne sont plus pertinentes — les pastilles Check ELS sous la card
  // montrent les todos restantes (transfert élec, etc.). On masque les dates.
  if (stage !== "ready_for_els" && stage !== "a_lancer" && stage !== "els_planned") return null;

  const s = checkElsStates(bien);
  // Une ligne par process DEMANDÉ dans ce cycle et pas encore fait — plus la
  // validité du certificat pour le PEB (un PEB demandé sur un certificat
  // encore valide n'avait pas de ligne, HK_H4.4 2026-09-15), et plus de ligne
  // ELS « à définir » sur un cycle PEB seul.
  const bd = bien as unknown as Record<string, unknown>;
  const elsRequired = isElsLance(bd);
  const pebRequired = isPebLance(bd) && !isPebDone(bd);
  const now = new Date();
  const fmt = (d: Date, h?: string | null) =>
    `${d.toLocaleDateString(localeFromLng(i18n.language))}${h ? ` · ${h}` : ""}`;

  // Assignation ELS active = prestataireEle ("Prestataire assigné") ; prestataireEls = trace.
  const elsExpert = expertLabel(bien.prestataireEle, collabMap, t);
  const pebExpert = expertLabel(bien.expertPeb, collabMap, t);
  const elsDate = bien.edlDatePrevue ? new Date(bien.edlDatePrevue) : null;
  const pebDate = bien.pebDatePrevue ? new Date(bien.pebDatePrevue) : null;

  const lines: { key: string; label: string; tone: DateTone; expert: string | null }[] = [];

  if (stage === "ready_for_els" || stage === "a_lancer") {
    // Col 1 : VERT si la date est prévue, ROUGE sinon — avec la raison :
    // personne à désigner, mission à lancer, ou rendez-vous à fixer (2026-09-15).
    const special = (id?: string | null) => id === "amiable" || id === "autre";
    const todoLabel = (assignee: string | null | undefined, launchedAt: unknown, defineKey: string) =>
      !assignee ? t(defineKey)
        : !launchedAt && !special(assignee) ? t("v2.pipeline.dates.toLaunch")
        : t("v2.pipeline.dates.toSchedule");
    if (elsRequired) {
      lines.push(
        elsDate
          ? { key: "ELS", label: fmt(elsDate, bien.edlHeure), tone: "green", expert: elsExpert }
          : { key: "ELS", label: todoLabel(bien.prestataireEle, bien.elsMissionLanceeAt, "v2.pipeline.dates.defineEls"), tone: "red", expert: elsExpert },
      );
    }
    if (pebRequired) {
      lines.push(
        pebDate
          ? { key: "PEB", label: fmt(pebDate, bien.pebHeure), tone: "green", expert: pebExpert }
          : { key: "PEB", label: todoLabel(bien.expertPeb, bien.pebMissionLanceeAt, "v2.pipeline.dates.definePeb"), tone: "red", expert: pebExpert },
      );
    }
  } else {
    // Col 2 (els_planned) : dates prises. ROUGE si la date est PASSÉE et que
    // l'expertise n'est pas validée (RDV dépassé, en attente). Sinon neutre.
    if (elsRequired && elsDate) {
      const past = !isNaN(elsDate.getTime()) && elsDate < now && s.els !== "success";
      lines.push({ key: "ELS", label: fmt(elsDate, bien.edlHeure), tone: past ? "red" : "muted", expert: elsExpert });
    }
    if (pebRequired && pebDate) {
      const expertiseOk = getPebStatut(bien as Parameters<typeof getPebStatut>[0]) === "expertise_ok";
      const past = !isNaN(pebDate.getTime()) && pebDate < now && !expertiseOk;
      lines.push({ key: "PEB", label: fmt(pebDate, bien.pebHeure), tone: past ? "red" : "muted", expert: pebExpert });
    }
  }

  const libreLabel = bien.seraDispoPour ? formatDate(bien.seraDispoPour, i18n.language) : null;
  if (lines.length === 0 && !libreLabel) return null;

  const toneText: Record<DateTone, string> = { green: "text-success", red: "text-danger", muted: "text-text-muted" };
  const toneTag: Record<DateTone, string> = { green: "text-success/70", red: "text-danger/70", muted: "text-text-muted/70" };

  return (
    <div className="flex flex-col gap-0.5 mt-1">
      {lines.map((l) => (
        <div key={l.key} className={cn("flex items-center gap-1 text-[11px]", toneText[l.tone])}>
          <Calendar size={10} />
          <span>
            <span className={toneTag[l.tone]}>{l.key}</span> {l.label}{l.expert ? ` (${l.expert})` : ""}
          </span>
        </div>
      ))}
      {libreLabel && (
        <div className="flex items-center gap-1 text-[11px] text-text-muted">
          <Calendar size={10} />
          <span>
            <span className="text-text-muted/70">{t("v2.pipeline.dates.freeOn")}</span> {libreLabel}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Sub-component : badges remarques (PEB / ELS / SRS) ────────────────────

interface RemarqueBadgesProps {
  pebCommentaire?: string | null;
  edlCommentaire?: string | null;
  commentaireSrs?: string | null;
}

function RemarqueBadges({ pebCommentaire, edlCommentaire, commentaireSrs }: RemarqueBadgesProps) {
  const { t } = useTranslation();
  const items: { label: string; text: string }[] = [];
  if (pebCommentaire && pebCommentaire.trim()) items.push({ label: t("v2.pipeline.remarques.peb"), text: pebCommentaire.trim() });
  if (edlCommentaire && edlCommentaire.trim()) items.push({ label: t("v2.pipeline.remarques.els"), text: edlCommentaire.trim() });
  if (commentaireSrs && commentaireSrs.trim()) items.push({ label: t("v2.pipeline.remarques.srs"), text: commentaireSrs.trim() });

  if (items.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-border/40 flex flex-wrap gap-1">
      {items.map((r) => (
        <span
          key={r.label}
          title={r.text}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-danger/10 text-danger cursor-help"
        >
          <MessageSquareWarning size={10} />
          {r.label}
        </span>
      ))}
    </div>
  );
}
